import React, { useCallback, useMemo } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';

import { datadogRum } from '../../hooks/useDatadog';
import { ErrorContainer, type IMainErrorProps, MainError } from '../MainError/MainError';
import { store } from '../Store/configureStore';
import { setGlobalError } from '../Store/errorBoundarySlice';
/**
 * Higher-Order Component (HOC) that wraps a component with an Error Boundary.
 *
 * Signature preserved for backward compatibility:
 *   withPLIErrorBoundary(WrappedComponent, FallbackComponent?, fallbackProps?)
 *
 * - If `FallbackComponent` is provided (not null/undefined), it's rendered directly on error.
 * - Otherwise, `MainError` is used by default.
 * - `fallbackProps` (Partial<IMainErrorProps>) are merged with sensible defaults.
 */
function withPLIErrorBoundary<P extends object>(
    WrappedComponent: React.ComponentType<P>,
    FallbackComponent?: React.ComponentType<IMainErrorProps> | null,
    fallbackProps?: Partial<IMainErrorProps>
) {
    const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

    // Default props for MainError (can be overridden via `fallbackProps`)
    const defaultFallbackProps: IMainErrorProps = {
        errorContainer: ErrorContainer.PlayerModal,
        marginRequired: false,
        noHelpInfo: false,
        children: undefined,
    };

    // Choose which fallback to use (MainError by default)
    //const errorToUse = fallbackProps?.errorContainer == ErrorContainer.PlayerModal ? MainErrorModal : MainError;
    //const FallbackToUse = FallbackComponent ?? errorToUse;
    const FallbackToUse = FallbackComponent ?? MainError;

    const WithErrorBoundary: React.FC<P> = (props) => {
        // Merge default fallback props with the caller's overrides
        const mergedFallbackProps: IMainErrorProps = useMemo(() => ({
            ...defaultFallbackProps,
            ...(fallbackProps ?? {}),
        }), []);

        /** Error logging callback: sends exceptions to Application Insights */
        const onError = useCallback((error: Error) => {
            datadogRum.addError(error, {
                severityLevel: 3, // eSeverityLevel.Error equivalent in legacy, keeping property for context
            });

            // For PlayerModal errors, set global error state to hide app content
            if (mergedFallbackProps.errorContainer === ErrorContainer.PlayerModal) {
                store.dispatch(setGlobalError(mergedFallbackProps));
            }
        }, [mergedFallbackProps]);

        /**
     * Internal fallback used by ErrorBoundary:
     * - If a custom FallbackComponent was provided, render it directly.
     * - Otherwise, render MainError centered on the viewport.
     */
        const Fallback: React.FC<FallbackProps> = () => {
            if (FallbackComponent) {
                // Custom fallback: render directly (no centering wrapper)
                return <FallbackToUse {...mergedFallbackProps} />;
            }

            // Default MainError fallback: center it on the screen
            return (
                <div
                    className="d-flex justify-content-center align-items-center w-100"
                    style={{ minHeight: '100vh', overflow: 'hidden' }}
                >
                    <MainError {...mergedFallbackProps} />
                </div>
            );
        };

        return (
            <ErrorBoundary FallbackComponent={Fallback} onError={onError}>
                <WrappedComponent {...(props as P)} />
            </ErrorBoundary>
        );
    };

    WithErrorBoundary.displayName = `withPLIErrorBoundary(${displayName})`;
    return WithErrorBoundary;
}

export default withPLIErrorBoundary;
